import React, { useEffect, useMemo } from 'react';

// DEPENDENCIES
import identity from 'lodash/identity';
import moment from 'moment';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useFormikContext } from 'formik';

// HELPERS
import { withRouter } from 'helpers/withRouter';

// GLOBAL VARIABLES
import { NEW_USED } from 'codes';
import { INVENTORY_FILE_TYPE_IDS, NEW_USED_TYPES } from 'globals.js';

// LOCAL VARIABLES
import {
  INVENTORY_SOURCE_IDS,
  LOCATION_TYPE_IDS,
  LOOKUP_PARAMS,
  INVENTORY_SOURCE_OPTION_KEYS,
  SALES_USER_OPTION_KEYS,
  INVENTORY_LOCATIONS_OPTION_KEYS,
  VEHICLE_MAKES_OPTION_KEYS,
  WARRANTY_TYPES_OPTION_KEYS
} from './variables';

// HELPERS
import useRecordView from 'helpers/getRecordView';

// ACTIONS
import { lookupActions } from 'actions.js';

// GLOBAL COMPONENTS
import Card from 'parts/card/Card';
import Form from 'core/form/Form';
import useGetDealerSettings from '../routes/settings/hooks/useGetDealerSettings';

// LOCAL COMPONENTS
import StockNumber from './parts/StockNumber';
import VinNumber from './parts/VinNumber';
import Attachment from 'routers/interface/tools/attachment/Attachment';

const TODAY = new Date();

// MAIN COMPONENT
const VehicleInfoBody = compose(
  withRouter,
  connect(({ lookups }) => ({ lookups }), { ...lookupActions })
)(({
  location: { search },
  // REDUX
  lookups: { exteriorColors, interiorColors },
  flush,
}) => {
  const { isAdd, isEdit } = useRecordView();
  const { values, setFieldValue } = useFormikContext() || {};
  const {
    inventory_source_id,
    located_at_dealership,
    location_type_id,
    new_used = 1,
    is_order = 0
  } = useMemo(() => values || {}, [values]);

  const params = new URLSearchParams(search);
  const newUsedParam = params.get('new_used');
  const newUsed = newUsedParam ? +newUsedParam : new_used;
  const newUsedLabel = NEW_USED_TYPES[new_used];
  const isNew = useMemo(() => newUsed === NEW_USED.new.numeric, [newUsed]);
  const { make_ids = '' } = useGetDealerSettings(isNew);

  const makeOption = useMemo(() => make_ids?.split(',')?.length === 1, [make_ids]);

  const isPurchaseFromCustomer = useMemo(
    () => +inventory_source_id === INVENTORY_SOURCE_IDS.PURCHASE_FROM_CUSTOMER_FOR_INVENTORY,
    [inventory_source_id]
  );
  const isManufacturer = useMemo(
    () => +inventory_source_id === INVENTORY_SOURCE_IDS.MANUFACTURER,
    [inventory_source_id]
  );
  const isTrade = useMemo(() => +inventory_source_id === INVENTORY_SOURCE_IDS.TRADE, [inventory_source_id]);
  const isCAD = useMemo(() => +location_type_id === LOCATION_TYPE_IDS.CAD, [location_type_id]);
  const isLocatedAtDealership = useMemo(
    () => (located_at_dealership ? +located_at_dealership === 1 : isAdd),
    [located_at_dealership, isAdd]
  );
  const isOrder = parseInt(is_order) === 1;

  useEffect(() => {
    if (isNew) {
      if (makeOption) {
        setFieldValue('make_id', make_ids);
      }
    }
    setFieldValue('new_used', newUsed);
  }, [setFieldValue, isAdd, isNew, newUsed, makeOption, make_ids]);

  // Reset on mount
  useEffect(() => {
    flush('exteriorColors');
    flush('interiorColors');
  }, [flush]);

  const lookupParams = useMemo(
    () => ({
      inventorySource: {
        newUsed: newUsed || '0'
      },
      inventoryLocation: {
        newUsed: newUsed,
        vehicleAtDealership: located_at_dealership
      }
    }),
    [newUsed, located_at_dealership]
  );

  const handleLocationChange = () => {
    setFieldValue('location_type_id', '');
  };

  return (
    <Form.Body>
      <Form.Control label="Type" value={newUsedLabel} plaintext />
      <Form.Control name="date_created" label="Date Added" plaintext />
      <Form.Control name="new_used" hidden />

      {isAdd && (
        <>
          <Form.Date
            name="purchase_date"
            label="Purchase Date"
            text="* Must be the date on the Seller's Bill of Sale"
            maxDate={TODAY}
            required
          />
          <Form.Select
            name="inventory_source_id"
            label="Source"
            lookup={isNew ? 'inventorySourceNew' : 'inventorySourceUsed'}
            lookupParams={lookupParams.inventorySource}
            optionKeys={INVENTORY_SOURCE_OPTION_KEYS}
            required
            useNone
          />
          <Form.Control
            name="source_name"
            label="Source Name"
            hidden={newUsed && +inventory_source_id !== 6 && +inventory_source_id !== 8}
          />
          <Form.Select
            name="buyer_user_id"
            label="Acquired By - Sales Manager"
            lookup="salesManagers"
            lookupParams={LOOKUP_PARAMS.salesManagerStaff}
            optionKeys={SALES_USER_OPTION_KEYS}
            required={!isManufacturer}
            hidden={(isNew && !+inventory_source_id) || isManufacturer}
            useNone
          />
          <Form.Select
            name="acquired_salesperson_user_id"
            label="Acquired By - Salesperson (If Any)"
            lookup="salesStaff"
            lookupParams={LOOKUP_PARAMS.salesStaff}
            optionKeys={SALES_USER_OPTION_KEYS}
            dropdown={{ maxHeight: '30rem' }}
            hidden={!isPurchaseFromCustomer}
            useNone
          />
          <Form.Select
            name="appraised_by_user_id"
            label="Appraised By"
            lookup="salesManagers"
            lookupParams={LOOKUP_PARAMS.salesManagerStaff}
            optionKeys={SALES_USER_OPTION_KEYS}
            dropdown={{ maxHeight: '30rem' }}
            hidden={!isTrade}
            useNone
          />
        </>
      )}

      <Form.YesNo
        name="located_at_dealership"
        label="Is the Vehicle Physically At the Dealership?"
        onChange={handleLocationChange}
        required
      />
      {isEdit || (isAdd && !isLocatedAtDealership) ? (
        <Form.Checklist
          lookup={
            isLocatedAtDealership
              ? newUsed
                ? 'inventoryLocationsAtDealerNew'
                : 'inventoryLocationsAtDealerUsed'
              : newUsed
              ? 'inventoryLocationsNotAtDealerNew'
              : 'inventoryLocationsNotAtDealerUsed'
          }
          type="radio"
          name="location_type_id"
          label="Location"
          optionKeys={INVENTORY_LOCATIONS_OPTION_KEYS}
          lookupParams={lookupParams.inventoryLocation}
          lookupDeps={located_at_dealership}
          optionsMapper={options =>
            options.map(
              isLocatedAtDealership
                ? o => ({
                    label: (
                      <>
                        <div className="d-block">{o.label}</div>
                        <div className="small text-gray-500 pt-2">
                          {o.value === 3
                            ? 'NOTE: Payment made within 48 hours.'
                            : o.value === 4
                            ? 'NOTE: This status should only be used when a vehicle checked in physically has later moved off site or has been transported to an auction.'
                            : ''}
                        </div>
                      </>
                    ),
                    value: o.value
                  })
                : identity
            )
          }
          useNone
          required
          text={
            !isLocatedAtDealership && (
              <div className="small text-gray-500">
                <p className="mb-2">
                  NOTE: Accounting will Not Process Payment until the vehicle is Physically Checked In.
                </p>
                <p>An Inventory E-Post will be sent to Accounting when the vehicle has been Physically Checked In.</p>
              </div>
            )
          }
          list={{
            direction: 'vertical'
          }}
        />
      ) : null}

      {!isLocatedAtDealership ? (
        <Form.Date
          name="location_arrival_date"
          label="Estimated Date of Arrival for Physical Check-in"
          required
          currentDate={moment().format('MM.DD.YYYY')}
        />
      ) : null}
      {!isLocatedAtDealership || +location_type_id === 4 ? (
        <Form.Textarea name="location_comments" label="Location Comments" required={!isCAD} />
      ) : null}
      {isAdd && !isLocatedAtDealership ? (
        <Form.YesNo name="is_order" value={is_order} label="Is This an Order without a VIN?" required />
      ) : null}
      <Form.Control name="purchase_method_id" hidden />
      {(!isAdd || !isOrder) && <>
        <VinNumber makeOption={makeOption} make_ids={make_ids} isNew={isNew} />
        {!isOrder &&
          <Attachment.Upload
            label="Photo Proof of VIN"
            name="documentAttachment.vin_photo"
            documentType={isNew ? INVENTORY_FILE_TYPE_IDS.VIN_PHOTO_NEW : INVENTORY_FILE_TYPE_IDS.VIN_PHOTO_USED}
            requestedFor="Inventory"
          />
        }
        <StockNumber />
      </>}
      <Form.Control name="vehicle_year" label="Year" schema="year" required />
      {makeOption && isNew ? (
        <Form.Select
          name="make_id"
          label="Make"
          plaintext
          lookup="vehicleMakes"
          optionKeys={VEHICLE_MAKES_OPTION_KEYS}
        />
      ) : (
        <Form.Select
          name="make_id"
          label="Make"
          lookup="vehicleMakes"
          optionKeys={VEHICLE_MAKES_OPTION_KEYS}
          optionsMapper={
            (isAdd || isNew) &&
            isNew &&
            (options => options.filter(o => make_ids.split(',').includes(o.value.toString())))
          }
          useNone={isAdd}
          required
        />
      )}
      <Form.Control name="model" label="Model" required />
      <Form.Control name="model_code" label="Model Code" required={isNew} />
      <Form.Control name="vehicle_trim" label="Trim" required />
      <Form.Control name="body_style" label="Body Style" required />
      <Form.Select allowCustom name="ext_color" label="Exterior" options={exteriorColors} required key={exteriorColors?.length} />
      <Form.Select allowCustom name="int_color" label="Interior" options={interiorColors} required key={interiorColors?.length} />
      <Form.Control name="engine" label="Engine" required />
      <Form.Control name="engine_type" label="Engine Type" />
      <Form.Control name="transmission_name" label="Transmission" required />
      <Form.Control name="drivetrain_name" label="Drivetrain" required />
      <Form.Control name="mileage" label="Mileage" schema="miles" required />
      <Attachment.Upload
        label="Photo Proof of Mileage"
        name="documentAttachment.mileage_photo"
        documentType={isNew ? INVENTORY_FILE_TYPE_IDS.MILEAGE_PHOTO_NEW : INVENTORY_FILE_TYPE_IDS.MILEAGE_PHOTO_USED}
        requestedFor="Inventory"
      />
      <Form.Select
        name="warranty_type"
        optionKeys={WARRANTY_TYPES_OPTION_KEYS}
        label="Warranty Type"
        lookup="warrantyTypes"
        useNone={isAdd}
        required={!newUsed}
      />
    </Form.Body>
  );
});

// EXPORT
export default function VehicleInfo(props) {
  return <Card title="Vehicle Info" body={<VehicleInfoBody {...props} />} />;
}
